// iRECIST section, ported from renderIRECISTSection (source lines 6230-6518).
// SOM/nadir/delta/suggestion are computed server-side (clinical-data service,
// using the same @woof/shared computeIrecistSummary as the backend) and
// arrive via `derived.irecist` on the visit GET response - the frontend only
// renders them, it never recomputes. Lesion add/remove uses simple prompt()
// dialogs rather than the source's dedicated modal - a disclosed
// simplification of the input UX, not of the underlying data model.
import { escapeHtml } from "./dom.js";
import { renderField } from "./section-fields.js";
import { IRECIST_TIMEPOINTS, IRECIST_RESPONSES } from "@woof/shared";

function lesionTable(list: any[], type: string, readOnly: boolean, onRemove: (idx: number) => void): string {
  if (!list.length) return `<p class="muted" style="margin:6px 0;font-size:12px">Aucune lésion enregistrée.</p>`;
  const rows = list
    .map(
      (l, i) => `<tr>
      <td>${i + 1}</td><td>${escapeHtml(l.organ || "")}</td><td>${escapeHtml(l.location || "")}</td><td>${escapeHtml(l.method || "")}</td>
      ${type === "target" ? `<td>${l.diameter ?? "—"}</td>` : ""}
      ${readOnly ? "" : `<td><button class="btn btn-sm btn-danger" data-lesion-remove="${type}:${i}">×</button></td>`}
    </tr>`
    )
    .join("");
  return `<table class="data-table" style="margin-top:6px"><thead><tr><th>#</th><th>Organe</th><th>Localisation</th><th>Méthode</th>${type === "target" ? "<th>Diam. (mm)</th>" : ""}${readOnly ? "" : "<th></th>"}</tr></thead><tbody>${rows}</tbody></table>`;
}

export function renderIrecistSection(vk: string, v: Record<string, any>, v1Data: Record<string, any>, derived: any): string {
  const isBaseline = vk === "V1";
  const defaultTP: Record<string, string> = { V1: "Baseline", V4: "D30", V6: "D90", V9: "D180" };
  const currentTP = v.timepoint || defaultTP[vk] || "";

  const targets = isBaseline ? v.target_lesions || [] : v1Data.target_lesions || [];
  const nontargets = isBaseline ? v.nontarget_lesions || [] : v1Data.nontarget_lesions || [];
  const newLesions = v.new_lesions || [];

  const summary = derived?.irecist ?? {};
  const fmtSOM = (x: number | null) => (x === null || x === undefined ? "—" : `${x.toFixed(1)} mm`);
  const fmtPct = (x: number | null) => (x === null || x === undefined ? "—" : `${x >= 0 ? "+" : ""}${x.toFixed(1)} %`);

  const optTP = ["", ...IRECIST_TIMEPOINTS.map((t) => t.code)].map((c) => {
    const label = IRECIST_TIMEPOINTS.find((t) => t.code === c)?.label ?? "— Sélectionner —";
    return `<option value="${c}" ${currentTP === c ? "selected" : ""}>${label}</option>`;
  }).join("");
  const optResp = ["", ...IRECIST_RESPONSES.map((r) => r.code)].map((c) => {
    const label = IRECIST_RESPONSES.find((r) => r.code === c)?.label ?? "— Sélectionner —";
    return `<option value="${c}" ${v.response === c ? "selected" : ""}>${label}</option>`;
  }).join("");

  const somCurrentField = isBaseline
    ? `<input type="text" value="${fmtSOM(summary.somCurrent)}" disabled style="background:#f3f4f6;font-weight:600;color:#1e3a8a">`
    : `<input type="number" min="0" step="0.1" data-fkey="som_target" value="${v.som_target ?? ""}" placeholder="Saisir le SOM mesuré">`;

  const suggestion = summary.suggestion;
  const suggestionColors: Record<string, { bg: string; border: string; text: string; icon: string }> = {
    iCR: { bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46", icon: "✓" },
    iPR: { bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46", icon: "✓" },
    iSD: { bg: "#eff6ff", border: "#93c5fd", text: "#1e3a8a", icon: "≈" },
    iUPD: { bg: "#fffbeb", border: "#fde68a", text: "#92400e", icon: "⚠" },
    iCPD: { bg: "#fef2f2", border: "#fca5a5", text: "#7f1d1d", icon: "⛔" },
    NE: { bg: "#f3f4f6", border: "#d1d5db", text: "#374151", icon: "ⓘ" },
  };
  let suggestionHtml = "";
  if (suggestion) {
    const c = suggestionColors[suggestion.code] ?? suggestionColors.NE!;
    suggestionHtml = `<div style="margin:12px 0;padding:10px 14px;background:${c.bg};border:1px solid ${c.border};border-radius:8px;color:${c.text}">
      <strong>${c.icon} Suggestion iRECIST : ${escapeHtml(suggestion.label)} (${suggestion.code})</strong>
      <ul style="margin:6px 0 0 18px;font-size:12px">${suggestion.reasons.map((r: string) => `<li>${escapeHtml(r)}</li>`).join("")}</ul>
      ${v.response !== suggestion.code ? `<button class="btn btn-sm btn-secondary" id="btnApplyIrecistSuggestion" style="margin-top:6px">Appliquer la suggestion</button>` : ""}
    </div>`;
  }

  return `
    <div class="form-grid">
      <div class="form-group"><label>Timepoint</label><select data-fkey="timepoint">${optTP}</select></div>
      ${renderField("Date évaluation", "eval_date", "date", v.eval_date, true)}
      <div class="form-group"><label>SOM cible (mm)</label>${somCurrentField}</div>
      <div class="form-group"><label>SOM baseline (mm)</label><input type="text" value="${fmtSOM(summary.somBaseline)}" disabled style="background:#f3f4f6;font-weight:600"></div>
      <div class="form-group"><label>SOM nadir (mm)</label><input type="text" value="${fmtSOM(summary.somNadir)}" disabled style="background:#f3f4f6;font-weight:600"></div>
      <div class="form-group"><label>Δ vs baseline (%)</label><input type="text" value="${fmtPct(summary.deltaBaseline)}" disabled style="background:#f3f4f6;font-weight:600"></div>
      <div class="form-group"><label>Δ vs nadir (%)</label><input type="text" value="${fmtPct(summary.deltaNadir)}" disabled style="background:#f3f4f6;font-weight:600"></div>
      ${!isBaseline ? `<div class="form-group"><label>Statut lésions non-cibles</label><select data-fkey="nontarget_status">
        <option value="">— Sélectionner —</option>
        <option value="CR" ${v.nontarget_status === "CR" ? "selected" : ""}>CR (toutes disparues)</option>
        <option value="Non-CR/Non-PD" ${v.nontarget_status === "Non-CR/Non-PD" ? "selected" : ""}>Non-CR/Non-PD</option>
        <option value="PD" ${v.nontarget_status === "PD" ? "selected" : ""}>PD (progression)</option>
      </select></div>` : ""}
      <div class="form-group"><label>Réponse iRECIST</label><select data-fkey="response">${optResp}</select></div>
      ${renderField("Évaluateur (radiologue)", "evaluator", "text", v.evaluator, true)}
    </div>
    ${suggestionHtml}
    <div style="margin-top:18px;padding:12px;background:#fafbfc;border-radius:8px;border:1px solid var(--border)">
      <h4 style="margin:0 0 8px 0">🎯 Lésions cibles (${targets.length}${isBaseline ? "" : " — définies à V1"})</h4>
      ${lesionTable(targets, "target", !isBaseline, () => {})}
      ${isBaseline ? `<button class="btn btn-primary btn-sm" id="btnAddTargetLesion" style="margin-top:6px">+ Ajouter lésion cible</button>` : ""}
    </div>
    <div style="margin-top:12px;padding:12px;background:#fafbfc;border-radius:8px;border:1px solid var(--border)">
      <h4 style="margin:0 0 8px 0">⚪ Lésions non-cibles (${nontargets.length}${isBaseline ? "" : " — définies à V1"})</h4>
      ${lesionTable(nontargets, "nontarget", !isBaseline, () => {})}
      ${isBaseline ? `<button class="btn btn-secondary btn-sm" id="btnAddNontargetLesion" style="margin-top:6px">+ Ajouter lésion non-cible</button>` : ""}
    </div>
    <div style="margin-top:12px;padding:12px;background:#fef2f2;border-radius:8px;border:1px solid #fecaca">
      <h4 style="margin:0 0 8px 0;color:#7f1d1d">🆕 Nouvelles lésions (${newLesions.length})</h4>
      ${lesionTable(newLesions, "new", false, () => {})}
      <button class="btn btn-danger btn-sm" id="btnAddNewLesion" style="margin-top:6px">+ Ajouter nouvelle lésion</button>
    </div>
    ${renderField("Notes iRECIST", "notes", "textarea", v.notes, true)}`;
}

// Attaches lesion add/remove/apply-suggestion handlers. `getSection`/`saveSection`
// let the caller own the actual PUT + re-render cycle (shared with other sections).
export function attachIrecistHandlers(
  root: ParentNode,
  currentData: Record<string, any>,
  suggestion: any,
  saveSection: (patch: Record<string, any>) => void
): void {
  root.querySelector("#btnApplyIrecistSuggestion")?.addEventListener("click", () => {
    if (suggestion) saveSection({ response: suggestion.code });
  });
  root.querySelector("#btnAddTargetLesion")?.addEventListener("click", () => {
    const organ = prompt("Organe (ex: Lymph nodes) :");
    if (!organ) return;
    const diameter = Number(prompt("Diamètre (mm) :") || "0");
    const list = [...(currentData.target_lesions || []), { organ, diameter }];
    saveSection({ target_lesions: list });
  });
  root.querySelector("#btnAddNontargetLesion")?.addEventListener("click", () => {
    const organ = prompt("Organe :");
    if (!organ) return;
    const list = [...(currentData.nontarget_lesions || []), { organ }];
    saveSection({ nontarget_lesions: list });
  });
  root.querySelector("#btnAddNewLesion")?.addEventListener("click", () => {
    const organ = prompt("Organe :");
    if (!organ) return;
    const list = [...(currentData.new_lesions || []), { organ }];
    saveSection({ new_lesions: list });
  });
  root.querySelectorAll<HTMLElement>("[data-lesion-remove]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const [type, idxStr] = btn.dataset.lesionRemove!.split(":");
      const idx = Number(idxStr);
      const key = type === "target" ? "target_lesions" : type === "nontarget" ? "nontarget_lesions" : "new_lesions";
      const list = [...(currentData[key] || [])];
      list.splice(idx, 1);
      saveSection({ [key]: list });
    });
  });
}
